/* ---------------------------------------------------------------------------
   The exported geometry, kept on the device.

   THE PROBLEM IT SOLVES IS THE FIRST SECOND OF EVERY VISIT. A model's `.glb` is
   between a few hundred kilobytes and the 8 MB ceiling the endpoint enforces, and
   three of this app's views draw it: the record's hero (src/modeler/meshView.ts),
   the Site view on its terrain (src/map/scene3d.ts) and the Defect Inspector
   (src/inspector/scene.ts). Every one of them opened on an empty panel and waited
   for that download — every time, on the same bridge, on a connection that in
   Nepal is often one bar at the abutment. The bytes had already been on the device,
   sometimes minutes earlier. Nothing kept them.

   So: THE DEVICE ANSWERS FIRST, THE SERVER ANSWERS SECOND. A load resolves out of
   IndexedDB if anything is stored, which is a disk read and paints in the same
   frame as the panel. The network question — "is what I hold still current?" — is
   then asked in the background, off the path the user is waiting on.

   WHAT MAKES THE CHECK CHEAP IS THE ETAG, NOT A HASH WE INVENT. R2 already carries
   one per object, the endpoint already sent it, and it is a fingerprint OF THE
   BYTES — which is exactly the question, and a stronger answer than the model's
   `stateHash`. That hash says whether the model was edited since the export; it
   cannot say whether a re-export produced different triangles, it is absent on
   geometry derived from a CSiBridge source (`SOURCE_DERIVED`), and reading it
   costs a whole model-index request. `If-None-Match` costs one conditional GET
   that R2 itself resolves, and a match comes back as a 304 with no body: a few
   hundred bytes and no egress. See the conditional branch in
   functions/api/modeler/geometry.ts, which had to be taught to answer it.

   NOT THE BROWSER'S OWN HTTP CACHE, which `cache-control: no-cache` already gets
   us a revalidation from. Three things are wrong with leaving it there. It still
   blocks the first paint on a round trip. Its eviction is the browser's business
   and is the first thing to go when a phone is short of space, silently, with no
   way to ask what is left. And it answers the LOADER — it cannot hand the bytes to
   anything else, so nothing can hold them across the moment a panel opens.

   A SEPARATE DATABASE FROM THE WRITE QUEUE'S, AND THIS IS NOT TIDINESS. The queue
   (./queue.ts) opens `bridge-watch` at VERSION 1. Adding a store for meshes to it
   means version 2, and from that moment `indexedDB.open('bridge-watch', 1)` — the
   call the queue makes, in a released build, on a device that has not reloaded —
   fails with a VersionError. That is the field capture queue, holding inspections
   taken at a river with photographs attached, and it would be down to make a
   rendering cache tidy. src/modeler/meshPresence.ts has the longer account of what
   careless IndexedDB versioning did to this project once already.

   TWO OBJECT STORES, for one reason: `meta` is touched on every read to keep the
   eviction order, and rewriting a whole record to do that would rewrite the eight
   megabytes with it. So the bytes sit alone in `meshes` and are written once.

   NOTHING HERE THROWS FOR A STORAGE FAILURE. Private-mode Firefox refuses
   IndexedDB outright, a locked-down profile refuses the upgrade, a full disk
   refuses the write. Every one of those means "no cache on this device", which is
   the behaviour the app had before this file existed — the download. A cache that
   took the 3D view down when it could not cache would be worse than none.
   --------------------------------------------------------------------------- */

import { modelGeometryUrl } from './api.ts';

/** One model's geometry, as handed to a caller. */
export interface Geometry {
  /** The `.glb` container, ready for `GLTFLoader.parse`. */
  bytes: ArrayBuffer;
  /** The server's ETag for these bytes, or null where the response carried none. */
  etag: string | null;
  /** When the server says the geometry was exported, ISO, or null. */
  exportedAt: string | null;
  /** Whether these bytes were already on this device when they were asked for. */
  fromCache: boolean;
}

/* --------------------------- the seam --------------------------- */

/** What the eviction order and the freshness check are decided from. */
interface Meta {
  key: string;
  etag: string | null;
  exportedAt: string | null;
  /** Size of the stored container, so eviction can work to a byte budget. */
  bytes: number;
  /** When it was last handed to a view. The LRU key. */
  usedAt: number;
}

/**
 * The storage, behind a seam — IndexedDB in a browser, an in-memory store under
 * the tests, exactly as ./queue.ts does it and for the same reason: jsdom
 * implements none of IndexedDB, and the alternative was leaving this unchecked.
 */
export interface GeometryStore {
  read(key: string): Promise<{ bytes: ArrayBuffer; meta: Meta } | null>;
  write(bytes: ArrayBuffer, meta: Meta): Promise<void>;
  touch(key: string, usedAt: number): Promise<void>;
  remove(key: string): Promise<void>;
  /** Every entry's metadata, without its bytes. What eviction reads. */
  index(): Promise<Meta[]>;
}

const DB_NAME = 'bridge-watch-geometry';
const DB_VERSION = 1;
const MESHES = 'meshes';
const META = 'meta';

function indexedDbStore(): GeometryStore | null {
  try {
    if (typeof indexedDB === 'undefined' || indexedDB === null) return null;
  } catch {
    // Private-mode Firefox throws on the property access itself.
    return null;
  }

  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    const pending = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        /* Both stores, or neither. A database carrying one of them is a half-built
           schema every later call would have to test for; creating them in the same
           upgrade means the version alone says what is there. */
        if (!db.objectStoreNames.contains(MESHES)) db.createObjectStore(MESHES, { keyPath: 'key' });
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB refused to open.'));
      req.onblocked = () => reject(new Error('IndexedDB upgrade blocked by another tab.'));
    });
    /* A failed open must not be remembered as a failure for the life of the page:
       another tab holding an upgrade open is temporary, and the next view should
       get to try again rather than inherit one bad moment. */
    pending.catch(() => {
      if (dbPromise === pending) dbPromise = null;
    });
    dbPromise = pending;
    return pending;
  };

  return {
    read: (key) =>
      open().then(
        (db) =>
          new Promise((resolve, reject) => {
            /* ONE TRANSACTION OVER BOTH STORES. Two would let an eviction land
               between them and hand back bytes with somebody else's metadata —
               which is a stale ETag against current bytes, and that is a bridge
               that never refreshes again. */
            const tx = db.transaction([MESHES, META], 'readonly');
            const mesh = tx.objectStore(MESHES).get(key);
            const meta = tx.objectStore(META).get(key);
            tx.oncomplete = () => {
              const row = mesh.result as { key: string; bytes: ArrayBuffer } | undefined;
              const m = meta.result as Meta | undefined;
              /* Bytes with no metadata are an interrupted write, not a cache hit:
                 nothing can say whether they are current, so they are not used. */
              resolve(row?.bytes && m ? { bytes: row.bytes, meta: m } : null);
            };
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB read failed.'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB read aborted.'));
          }),
      ),

    write: (bytes, meta) =>
      open().then(
        (db) =>
          new Promise<void>((resolve, reject) => {
            const tx = db.transaction([MESHES, META], 'readwrite');
            /* The bytes and the metadata in ONE transaction, so the pair a reader
               can see is either both or neither. */
            tx.objectStore(MESHES).put({ key: meta.key, bytes });
            tx.objectStore(META).put(meta);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed.'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB write aborted.'));
          }),
      ),

    touch: (key, usedAt) =>
      open().then(
        (db) =>
          new Promise<void>((resolve, reject) => {
            const tx = db.transaction(META, 'readwrite');
            const store = tx.objectStore(META);
            const get = store.get(key);
            get.onsuccess = () => {
              const m = get.result as Meta | undefined;
              // Evicted between the read and here — nothing to keep alive.
              if (m) store.put({ ...m, usedAt });
            };
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB touch failed.'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB touch aborted.'));
          }),
      ),

    remove: (key) =>
      open().then(
        (db) =>
          new Promise<void>((resolve, reject) => {
            const tx = db.transaction([MESHES, META], 'readwrite');
            tx.objectStore(MESHES).delete(key);
            tx.objectStore(META).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed.'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB delete aborted.'));
          }),
      ),

    index: () =>
      open().then(
        (db) =>
          new Promise((resolve, reject) => {
            const tx = db.transaction(META, 'readonly');
            const all = tx.objectStore(META).getAll();
            all.onsuccess = () => resolve((all.result as Meta[]) ?? []);
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB index read failed.'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB index read aborted.'));
          }),
      ),
  };
}

/** An in-memory store, for the tests and for reasoning about the logic. */
export function memoryGeometryStore(): GeometryStore {
  const meshes = new Map<string, ArrayBuffer>();
  const metas = new Map<string, Meta>();
  return {
    read: async (key) => {
      const bytes = meshes.get(key);
      const meta = metas.get(key);
      return bytes && meta ? { bytes, meta: { ...meta } } : null;
    },
    write: async (bytes, meta) => {
      meshes.set(meta.key, bytes);
      metas.set(meta.key, { ...meta });
    },
    touch: async (key, usedAt) => {
      const meta = metas.get(key);
      if (meta) metas.set(key, { ...meta, usedAt });
    },
    remove: async (key) => {
      meshes.delete(key);
      metas.delete(key);
    },
    index: async () => [...metas.values()].map((m) => ({ ...m })),
  };
}

let store: GeometryStore | null | undefined;

function current(): GeometryStore | null {
  if (store === undefined) store = indexedDbStore();
  return store;
}

/** Swap the backing store. Used by the tests; null means "do not cache here". */
export function setGeometryStore(next: GeometryStore | null): void {
  store = next;
}

/* --------------------------- what is kept --------------------------- */

/**
 * How many models' geometry to keep, and how much of it.
 *
 * BOTH, because either alone is wrong for this app. An engineer works a corridor
 * of structures and comes back to the same handful for weeks, so a count keeps the
 * set that is actually in use warm. But the endpoint's ceiling is 8 MB per model,
 * and twelve of those on a field phone that also holds queued inspections and
 * their photographs is not a cache, it is a reason the device runs out of room.
 * Whichever bites first wins, and the least recently drawn goes.
 */
export const KEEP_ENTRIES = 12;
export const KEEP_BYTES = 64 * 1024 * 1024;

/** A full download, on the connection this app is actually used on. */
const DOWNLOAD_MS = 90_000;
/**
 * The background freshness check.
 *
 * Far shorter than the download, because nobody is waiting for this and a check
 * that hangs on a dead connection should give the socket back rather than sit on
 * it. Timing out costs nothing: the device keeps drawing what it holds and the
 * next mount asks again.
 */
const CHECK_MS = 10_000;

function keyOf(bridgeId: string, modelId: string): string {
  return `${bridgeId}/${modelId}`;
}

/**
 * An ETag with its HTTP clothes off, for comparing one with a `geometryVersion`.
 *
 * TWO FORMS OF ONE VALUE, AND THEY ARE NEVER EQUAL AS STRINGS. What is stored
 * beside the bytes is the `etag` header — quoted, and possibly marked weak,
 * because that is what a response carries. What the model index reports is R2's
 * `etag`, which is the bare hash. Comparing them as they stand is a test that
 * can only ever fail, and it would fail SILENTLY: every mount would go back to a
 * round trip and nothing would say why. `conditionOf` in
 * functions/api/modeler/geometry.ts undresses the header the same way at the
 * other end, and for the same reason.
 *
 * Exported for the checks — this is the line the optimisation lives or dies on.
 */
export function bareEtag(etag: string | null): string | null {
  if (!etag) return null;
  const weak = etag.startsWith('W/') ? etag.slice(2) : etag;
  const bare = weak.length > 1 && weak.startsWith('"') && weak.endsWith('"')
    ? weak.slice(1, -1)
    : weak;
  return bare || null;
}

/** The last stamp handed out, so the next one is never the same or smaller. */
let lastStamp = 0;

/**
 * A monotonic "when this was last drawn", for the eviction order.
 *
 * `Date.now()` alone is wrong here for a reason that only shows up in the case
 * eviction exists for: opening a corridor of structures one after another puts
 * several models in the store inside the same millisecond, they all carry the same
 * stamp, and the sort has no order to work with — so the victim is whichever the
 * store happened to list first, which can be the one just drawn. Bumping past the
 * clock when it has not moved keeps the order strictly by use while leaving the
 * value a wall-clock time, which is what makes stamps from an earlier session and
 * this one comparable at all.
 */
function stamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

/**
 * Bring the store back inside its budget, least recently drawn first.
 *
 * Runs after a write rather than on a timer or at boot: the moment something was
 * added is the only moment the budget can have been broken, and a cache that
 * cleans itself up at boot spends a field device's first seconds doing it.
 */
async function evict(s: GeometryStore): Promise<void> {
  let rows: Meta[];
  try {
    rows = await s.index();
  } catch {
    return; // cannot read the order, so cannot safely choose a victim
  }

  rows.sort((a, b) => b.usedAt - a.usedAt); // most recently drawn first
  let kept = 0;
  let bytes = 0;
  for (const row of rows) {
    kept += 1;
    bytes += row.bytes;
    if (kept <= KEEP_ENTRIES && bytes <= KEEP_BYTES) continue;
    try {
      await s.remove(row.key);
    } catch {
      /* Left in place. It is over budget, not corrupt, and the next write tries
         again — losing a view over a failed tidy-up would be the worse trade. */
    }
  }
}

/* --------------------------- the network --------------------------- */

type Answer =
  | { state: 'new'; bytes: ArrayBuffer; etag: string | null; exportedAt: string | null }
  /** The ETag matched: what the device holds is current. */
  | { state: 'same' }
  /** The object is gone from the bucket — a deleted model, or one never exported. */
  | { state: 'gone' };

/**
 * Ask the endpoint, conditionally where there is something to be conditional on.
 *
 * `cache: 'no-store'` keeps the browser's own cache out of the middle. It would
 * otherwise be free to answer a conditional request from its own copy, which turns
 * "the server says these bytes are current" into "some layer between here and the
 * server thinks so" — and this call's entire job is to be the authoritative one.
 *
 * Throws only where the caller can act on it: no answer at all, or a refusal. A
 * 304 and a 404 are both answers, and both come back as values.
 */
async function ask(
  bridgeId: string,
  modelId: string,
  etag: string | null,
  timeoutMs: number,
  /** The index's `geometryVersion`, where the caller had one. See `loadGeometryBytes`. */
  version?: string | null,
): Promise<Answer> {
  // jsdom under the smoke tests has no fetch unless one is stubbed in.
  if (typeof fetch !== 'function') throw new Error('No fetch available in this environment.');

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(modelGeometryUrl(bridgeId, modelId, version), {
      signal: controller.signal,
      /* STILL `no-store`, EVEN ON A VERSIONED URL, and the reason is storage
         rather than freshness. That URL is safe for the browser to hold for a
         year — the endpoint says so — but holding it would mean the same eight
         megabytes in the HTTP cache AND in IndexedDB, on a phone that is also
         carrying queued inspections and their photographs. One copy, in the
         store this module can read, evict and reason about.

         The saving the versioned URL actually banks is upstream of this call and
         not in it: where the index's version matches what is held, `ask` is
         never reached at all. */
      cache: 'no-store',
      ...(etag ? { headers: { 'if-none-match': etag } } : {}),
    });

    if (response.status === 304) return { state: 'same' };
    if (response.status === 404) return { state: 'gone' };
    if (!response.ok) {
      throw new Error(`The 3D geometry could not be read (HTTP ${response.status}).`);
    }

    const bytes = await response.arrayBuffer();
    /* An empty body is not geometry. Stored, it would be a permanent cache hit
       that parses into nothing on every visit — worse than no cache, because the
       device would then stop asking for the real file. */
    if (bytes.byteLength === 0) throw new Error('The 3D geometry came back empty.');

    return {
      state: 'new',
      bytes,
      etag: response.headers.get('etag'),
      exportedAt: response.headers.get('x-exported-at'),
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Store an answer, and tidy up after it. Never throws — see the header. */
async function keep(key: string, answer: Extract<Answer, { state: 'new' }>): Promise<void> {
  const s = current();
  if (!s) return;
  try {
    await s.write(answer.bytes, {
      key,
      etag: answer.etag,
      exportedAt: answer.exportedAt,
      bytes: answer.bytes.byteLength,
      usedAt: stamp(),
    });
    await evict(s);
  } catch {
    /* A full disk, a quota refusal, a profile that will not store. The view has its
       bytes either way; all that is lost is the head start next time. */
  }
}

/* --------------------------- the background check --------------------------- */

/**
 * Models already asked about on this page load.
 *
 * The check is worth making when a panel opens, not every time it re-opens: the
 * three views mount and unmount as somebody moves between the record, the Site view
 * and the Inspector, and each of those would otherwise be a round trip asking a
 * question answered a few seconds ago. A FAILED check is removed again, so a bad
 * moment on the connection does not settle the matter for the whole session.
 */
const asked = new Set<string>();

function revalidate(
  bridgeId: string,
  modelId: string,
  etag: string | null,
  onUpdated: ((fresh: Geometry) => void) | undefined,
  version?: string | null,
): void {
  const key = keyOf(bridgeId, modelId);
  if (asked.has(key)) return;
  asked.add(key);

  void (async () => {
    try {
      const answer = await ask(bridgeId, modelId, etag, CHECK_MS, version);
      if (answer.state === 'same') return;

      if (answer.state === 'gone') {
        /* The object is not in the bucket any more — the model was deleted, or its
           geometry was. Holding the last copy would draw a structure the register
           no longer has, on this one device, indefinitely. The views read
           `hasGeometry` off the index and stop asking; this makes sure that if they
           do ask, the answer is honest. */
        await dropGeometry(bridgeId, modelId);
        return;
      }

      await keep(key, answer);
      onUpdated?.({
        bytes: answer.bytes,
        etag: answer.etag,
        exportedAt: answer.exportedAt,
        fromCache: false,
      });
    } catch {
      /* Offline at the abutment, a timeout, a refused session. None of those is
         "your copy is stale", so nothing is dropped and nothing is said — the view
         keeps drawing what it has. Forgotten rather than remembered, so the next
         time this model is opened the question gets asked again. */
      asked.delete(key);
    }
  })();
}

/* --------------------------- the front door --------------------------- */

/** In-flight downloads, so two views opening on one model fetch it once. */
const inFlight = new Map<string, Promise<Geometry>>();

/**
 * A model's geometry: from this device if it is here, from the server if it is not.
 *
 * `onUpdated` is called ONLY when the background check found different bytes, and
 * only after they have been stored — so a caller that ignores it still gets the new
 * geometry on the next mount. A caller that takes it is choosing to swap what is on
 * screen, which is right for a view somebody is looking at a bridge in and wrong for
 * one they are pinning defects onto; the call sites say which they are.
 *
 * Throws only when there is nothing to draw: nothing cached and the network would
 * not supply it. Every 3D view already handles that, because it is what happens
 * offline today.
 */
export async function loadGeometryBytes(
  bridgeId: string,
  modelId: string,
  onUpdated?: (fresh: Geometry) => void,
  /**
   * The model index's `geometryVersion` for this model, where the caller has it.
   *
   * WHAT IT REPLACES IS A ROUND TRIP PER MOUNT. The stored copy carries the etag
   * it arrived with; this is the etag the bucket holds now, and it came free with
   * an index read the record was making anyway. Two strings agreeing is the same
   * answer the conditional GET came back with, arrived at without asking — and
   * these views mount and unmount every time somebody moves between the record,
   * the Site view and the Inspector.
   *
   * Two strings DISAGREEING is a stronger answer than the old path had: the copy
   * is known stale before it is drawn, so it is not drawn: the current bytes are
   * fetched first, and the held copy is used only if the network cannot answer.
   *
   * Omitted — the Site view before its index has landed, a caller with no summary
   * — and everything behaves exactly as it did: conditional GET, ETag, 304.
   */
  version?: string | null,
): Promise<Geometry> {
  const key = keyOf(bridgeId, modelId);
  const s = current();

  if (s) {
    let held: { bytes: ArrayBuffer; meta: Meta } | null = null;
    try {
      held = await s.read(key);
    } catch {
      held = null; // an unreadable cache is an absent one
    }
    if (held) {
      // Not awaited: the eviction order is bookkeeping, and the view is waiting.
      void s.touch(key, stamp()).catch(() => undefined);
      /* THE INDEX ALREADY ANSWERED, so nothing is asked. Only where both sides
         have a fingerprint: a stored copy with no etag, or an index entry with no
         version, is a pair that cannot be compared, and guessing from a
         half-comparison is how a device stops refreshing for ever. */
      /* THREE CASES AND NOT TWO, which is what the first attempt at this got
         wrong: no version at all is not the same as a version that disagrees.
         With no version this module knows exactly what it knew before — ask,
         once per page load — and treating that silence as staleness put a check
         behind every mount of every view, which is the cost `asked` exists to
         avoid. */
      const known = Boolean(version);
      const settled = known && bareEtag(version ?? null) === bareEtag(held.meta.etag);
      const fromHeld: Geometry = {
        bytes: held.bytes,
        etag: held.meta.etag,
        exportedAt: held.meta.exportedAt,
        fromCache: true,
      };
      if (settled) {
        // Asked and answered, by the index. Nothing more to learn this page load.
        asked.add(key);
        return fromHeld;
      }
      if (!known) {
        revalidate(bridgeId, modelId, held.meta.etag, onUpdated, version);
        return fromHeld;
      }

      /* KNOWN STALE, SO NOT DRAWN. The index positively says these are not the
         current bytes — a re-import replaced the `.glb` under the same model id — and
         handing them over anyway put the old bridge on screen in every view that
         takes no `onUpdated`, for the whole of the visit. Asked now, conditionally:
         a 304 means the copy was current after all and nothing is downloaded. The
         held copy is drawn only when the network cannot answer, because last week's
         bridge offline still beats an empty panel. */
      let answer: Answer;
      try {
        answer = await ask(bridgeId, modelId, held.meta.etag, DOWNLOAD_MS, version);
      } catch {
        return fromHeld;
      }
      asked.add(key);
      if (answer.state === 'same') return fromHeld;
      if (answer.state === 'gone') {
        await dropGeometry(bridgeId, modelId);
        throw new Error('The 3D geometry is not on the server.');
      }
      await keep(key, answer);
      return {
        bytes: answer.bytes,
        etag: answer.etag,
        exportedAt: answer.exportedAt,
        fromCache: false,
      };
    }
  }

  const running = inFlight.get(key);
  if (running) return running;

  const download = (async (): Promise<Geometry> => {
    const answer = await ask(bridgeId, modelId, null, DOWNLOAD_MS, version);
    /* A 304 cannot happen on an unconditional request — no ETag was sent, so there
       is nothing for one to be about — and a 404 here means there is no geometry to
       draw at all, which is the caller's failure case rather than a value. */
    if (answer.state !== 'new') throw new Error('The 3D geometry is not on the server.');
    await keep(key, answer);
    /* Asked and answered: these bytes came from the server a moment ago, so the
       background check has nothing to add for the rest of this page load. */
    asked.add(key);
    return {
      bytes: answer.bytes,
      etag: answer.etag,
      exportedAt: answer.exportedAt,
      fromCache: false,
    };
  })();

  inFlight.set(key, download);
  try {
    return await download;
  } finally {
    inFlight.delete(key);
  }
}

/**
 * Forget a model's cached geometry.
 *
 * Called after this device re-exports geometry (src/modeler/geometry.ts): the PUT
 * replaced the object, so what is stored here is now certainly stale and the ETag
 * beside it certainly wrong. Dropping it is better than storing the bytes that were
 * just uploaded, because the PUT's response does not carry the new ETag — the next
 * view downloads once and stores the pair together.
 */
export async function dropGeometry(bridgeId: string, modelId: string): Promise<void> {
  const key = keyOf(bridgeId, modelId);
  asked.delete(key);
  const s = current();
  if (!s) return;
  try {
    await s.remove(key);
  } catch {
    /* Nothing to remove, or a store that will not. The ETag check catches it on the
       next mount, which is the slower of the two correct outcomes. */
  }
}

/* --------------------------- parsing --------------------------- */

/**
 * Parse cached `.glb` bytes with a loader the caller has already imported.
 *
 * THE EMPTY PATH IS THE POINT OF THIS BEING A FUNCTION. `GLTFLoader.parse` uses it
 * to resolve external buffers and images, and a wrong one turns a self-contained
 * container into a set of failed requests. Every `.glb` this app stores IS
 * self-contained — the server derives one binary chunk and no image URIs
 * (functions/lib/meshGlb.ts), and three.js's own exporter embeds everything when it
 * writes binary — so there is nothing to resolve and the honest path is empty. Said
 * once here rather than three times over at the call sites.
 *
 * Structurally typed rather than importing `GLTFLoader`: three.js is a 600 kB
 * on-demand chunk in every view that draws, and naming the class here as a value
 * would pull it into the register's own bundle.
 */
export function parseGlb<T>(
  loader: { parseAsync(data: ArrayBuffer, path: string): Promise<T> },
  bytes: ArrayBuffer,
): Promise<T> {
  return loader.parseAsync(bytes, '');
}
